import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const HERO_BG = "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=1920&q=85";

export default function Login() {
    const { login, formatApiError } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back!");
            navigate("/dashboard");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid lg:grid-cols-2">
            <div className="relative hidden lg:block">
                <img src={HERO_BG} alt="Fiji rolling hills" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-ocean-deep/60" />
                <div className="relative z-10 flex flex-col justify-end h-full p-12 text-white">
                    <h2 className="font-display text-4xl font-bold leading-tight mb-3">Bula vinaka.<br />Welcome back.</h2>
                    <p className="font-app text-white/85 max-w-md">Sign in to access your Fijian land portfolio, Joint Venture submissions, and secure messages with verified parties.</p>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <Card className="w-full max-w-md p-8">
                    <h1 className="font-display text-3xl font-bold mb-2">Sign in</h1>
                    <p className="text-sm text-muted-foreground font-app mb-6">Welcome back to Bula Real Estate Fiji.</p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1.5" />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <div className="relative mt-1.5">
                                <Input id="password" data-testid="login-password-input" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                                <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3 font-app">{error}</div>
                        )}

                        <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full bg-ocean-green text-white hover:opacity-90 font-app h-11">
                            {loading ? "Signing in…" : "Sign in"}
                        </Button>
                    </form>

                    <p className="text-sm text-center text-muted-foreground mt-6 font-app">
                        New here? <Link to="/register" className="text-coral font-medium hover:underline">Create an account</Link>
                    </p>
                </Card>
            </div>
        </div>
    );
}
